import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import FloatingWhatsapp from "@/components/site/FloatingWhatsapp";
import Hero from "@/components/site/Hero";
import TrustMetrics from "@/components/site/TrustMetrics";
import Problem from "@/components/site/Problem";
import Journey from "@/components/site/Journey";
import Programs from "@/components/site/Programs";
import Skills from "@/components/site/Skills";
import WhyUs from "@/components/site/WhyUs";
import Founders from "@/components/site/Founders";
import Colleges from "@/components/site/Colleges";
import Employers from "@/components/site/Employers";
import Testimonials from "@/components/site/Testimonials";
import Faq from "@/components/site/Faq";
import Contact from "@/components/site/Contact";

export default function Home() {
  const [site, setSite] = useState(null);
  const [seo, setSeo] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [skills, setSkills] = useState([]);
  const [founders, setFounders] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [tests, setTests] = useState([]);

  useEffect(() => {
    (async () => {
      const [siteR, seoR, progR, skillR, fndR, faqR, tR] = await Promise.all([
        api.get("/content/site").catch(() => ({ data: null })),
        api.get("/content/seo").catch(() => ({ data: null })),
        api.get("/content/programs").catch(() => ({ data: [] })),
        api.get("/content/skills").catch(() => ({ data: [] })),
        api.get("/content/founders").catch(() => ({ data: [] })),
        api.get("/content/faqs").catch(() => ({ data: [] })),
        api.get("/content/testimonials").catch(() => ({ data: [] })),
      ]);
      setSite(siteR.data); setSeo(seoR.data);
      setPrograms(progR.data || []); setSkills(skillR.data || []);
      setFounders(fndR.data || []); setFaqs(faqR.data || []);
      setTests(tR.data || []);
    })();
  }, []);

  useEffect(() => {
    if (!seo) return;
    const CANONICAL_ORIGIN = "https://behireready.in";
    const pageUrl = `${CANONICAL_ORIGIN}${window.location.pathname === "/" ? "/" : window.location.pathname}`;
    document.title = seo.title || "Be Hire Ready | Interview Preparation & Career Readiness in Jodhpur";

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };

    setMeta("description", seo.description);
    setMeta("keywords", seo.keywords);
    setMeta("robots", "index, follow, max-image-preview:large");
    setLink("canonical", seo.canonical_url || pageUrl);
    setMeta("og:title", seo.title, "property");
    setMeta("og:description", seo.description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", pageUrl, "property");
    setMeta("og:site_name", "Be Hire Ready", "property");
    setMeta("og:locale", "en_IN", "property");
    if (seo.og_image) setMeta("og:image", seo.og_image, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", seo.title);
    setMeta("twitter:description", seo.description);
    if (seo.og_image) setMeta("twitter:image", seo.og_image);
    if (seo.gsc_verification) setMeta("google-site-verification", seo.gsc_verification);

    // JSON-LD: Organization + LocalBusiness + FAQ + Programs (Courses)
    let ld = document.getElementById("bhr-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "bhr-jsonld"; document.head.appendChild(ld); }

    const sameAs = [site?.instagram].filter(Boolean);
    const org = {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "@id": `${CANONICAL_ORIGIN}/#organization`,
      "name": "Be Hire Ready",
      "url": CANONICAL_ORIGIN,
      "description": seo.description,
      "logo": site?.logo_url,
      "telephone": site?.phone,
      "email": site?.email,
      "sameAs": sameAs,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Jodhpur",
        "addressRegion": "Rajasthan",
        "addressCountry": "IN",
      },
      "areaServed": "IN",
      "founder": (founders || []).map((f) => ({
        "@type": "Person", "name": f.name, "jobTitle": f.designation, "image": f.photo_url,
      })),
    };

    const localBiz = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${CANONICAL_ORIGIN}/#localbusiness`,
      "name": "Be Hire Ready",
      "image": site?.logo_url,
      "url": CANONICAL_ORIGIN,
      "telephone": site?.phone,
      "priceRange": "₹₹",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Jodhpur",
        "addressRegion": "Rajasthan",
        "addressCountry": "IN",
      },
      "sameAs": sameAs,
    };

    const website = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${CANONICAL_ORIGIN}/#website`,
      "url": CANONICAL_ORIGIN,
      "name": "Be Hire Ready",
      "publisher": { "@id": `${CANONICAL_ORIGIN}/#organization` },
      "inLanguage": "en-IN",
    };

    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": (faqs || []).filter((f) => f.published !== false).map((f) => ({
        "@type": "Question", "name": f.question,
        "acceptedAnswer": { "@type": "Answer", "text": f.answer },
      })),
    };

    const courses = (programs || []).filter((p) => p.active !== false).map((p) => ({
      "@context": "https://schema.org",
      "@type": "Course",
      "name": p.name,
      "description": p.description || p.tagline,
      "provider": { "@id": `${CANONICAL_ORIGIN}/#organization` },
    }));

    ld.textContent = JSON.stringify([org, localBiz, website, faqLd, ...courses]);
  }, [seo, site, faqs, founders, programs]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar logoUrl={site?.logo_url} />
      <main>
        <Hero site={site || {}} />
        <TrustMetrics site={site || {}} />
        <Problem />
        <Journey />
        <Programs programs={programs} />
        <Skills skills={skills} />
        <WhyUs />
        <Founders founders={founders} />
        <Colleges />
        <Employers />
        <Testimonials testimonials={tests} />
        <Faq faqs={faqs} />
        <Contact site={site || {}} />
      </main>
      <Footer site={site || {}} />
      <FloatingWhatsapp number={site?.whatsapp} />
    </div>
  );
}
